import { Resources } from 'constants/resources';

import { Skeleton } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { deleteMerchantAccesses } from 'config/requests/merchants';
import { queryClient } from 'providers/queryClient';
import { useGetMany, useListContext, useNotify } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { MerchantAccess } from 'types/embedded/merchantAccess';
import { User } from 'types/embedded/user';
import { getId } from 'utils';

import {
  BoxStyled,
  DialogStyled,
  FormBoxStyled,
  ListStyled,
} from './MerchantShowAccessDisableDialog.styled';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedAccesses: MerchantAccess[];
  merchantName: string | undefined;
  merchantId: string | undefined;
};

export const MerchantShowAccessDisableDialog: React.FC<Props> = ({
  open,
  onClose,
  merchantId,
  merchantName,
  selectedAccesses,
}) => {
  const { onUnselectItems } = useListContext();
  const notify = useNotify();
  const form = useForm();

  const usersIds = selectedAccesses?.map((access) => access?.user.id);
  const { data = [], isLoading } = useGetMany<User>(
    Resources.Users,
    { ids: usersIds },
    { enabled: !!usersIds.length },
  );

  const { mutate } = useMutation({
    mutationFn: ({ merchant, users }: { merchant: number; users: number[] }) =>
      deleteMerchantAccesses(merchant, users),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [`${Resources.Merchants}/${getId(merchantId)}/merchant_accesses`],
      });
      onUnselectItems();
      onClose();
    },
    onError: (error: any) => {
      notify(error.data.errors[0].title || 'Что-то пошло не так. Повторите запрос позже.', {
        type: 'error',
      });
    },
  });

  const onSubmit = () => {
    mutate({
      merchant: Number(getId(merchantId)),
      users: data.map((user) => Number(getId(user.id))),
    });
  };

  return (
    <DialogStyled onClose={onClose} open={open} width="640px">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <BoxStyled>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title="Отключить доступ"
              titleSize="smallBold"
            />
            <CardContent>
              <FormBoxStyled>
                <Alert severity="warning" sx={{ marginBottom: '24px' }}>
                  {`ВНИМАНИЕ! После отключения доступа, мерчант ${merchantName} будет недоступен для выбранных
                пользователей!`}
                </Alert>
                <Typography>Пользователи:</Typography>
                <ListStyled>
                  {isLoading
                    ? usersIds.map((userId) => (
                        <li key={userId}>
                          <Skeleton height={24} variant="text" width="100%" />
                        </li>
                      ))
                    : data?.map((user) => {
                        return <li key={user.id}>{`${user.firstName} ${user.lastName}`}</li>;
                      })}
                </ListStyled>
              </FormBoxStyled>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton key="cancel-button" onClick={onClose} variant="outlined" />,
                <Button
                  color="error"
                  key="save-button"
                  label="Отключить"
                  type="submit"
                  variant="contained"
                />,
              ]}
            />
          </BoxStyled>
        </form>
      </FormProvider>
    </DialogStyled>
  );
};
